import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { listIngredients } from '../../api/ingredients.js';
import type { Ingredient } from '../../api/types.js';

export interface IngredientAutocompleteProps {
  value: number | null;
  onChange: (id: number | null) => void;
  onCreateRequested?: (name: string) => void;
  placeholder?: string;
  testId?: string;
}

export function IngredientAutocomplete({
  value,
  onChange,
  onCreateRequested,
  placeholder,
  testId,
}: IngredientAutocompleteProps): ReactElement {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<Ingredient[]>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      let cancelled = false;
      listIngredients({ search: query }).then((rows) => {
        if (!cancelled) setOptions(rows);
      }).catch(() => {
        if (!cancelled) setOptions([]);
      });
      return () => {
        cancelled = true;
      };
    }, 200);
    return () => {
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    };
  }, [query]);

  useEffect(() => {
    if (value === null) return;
    if (query.length > 0) return;
    let cancelled = false;
    listIngredients({}).then((rows) => {
      if (cancelled) return;
      const found = rows.find((r) => r.id === value);
      if (found) setQuery(found.name);
    }).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [value, query.length]);

  const selected = useMemo(
    () => options.find((o) => o.id === value) ?? null,
    [options, value],
  );

  const exactMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return true;
    return options.some((o) => o.name.toLowerCase() === q);
  }, [options, query]);

  const trimmed = query.trim();
  // We deliberately suppress `+ Create "<q>"` when an ingredient with the same
  // trimmed, case-insensitive name already exists. Duplicate creation is
  // rejected by the backend (409 INGREDIENT_DUPLICATE), so hiding the action
  // here avoids a guaranteed-failure dialog round-trip.
  const showCreate = trimmed.length > 0 && !exactMatch && onCreateRequested !== undefined;

  function pick(o: Ingredient): void {
    onChange(o.id);
    setQuery(o.name);
    setOpen(false);
  }

  return (
    <div className="relative w-full" data-testid={testId}>
      <input
        type="text"
        value={query}
        placeholder={placeholder ?? 'Ingredient'}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
          if (selected && e.target.value !== selected.name) {
            onChange(null);
          }
        }}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, options.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === 'Enter') {
            const target = options[highlight];
            if (target) {
              e.preventDefault();
              pick(target);
            }
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        className="w-full rounded-md border px-3 py-2 text-sm"
        style={{
          background: 'var(--bg-primary, transparent)',
          borderColor: 'var(--border)',
          color: 'var(--text-main)',
        }}
      />
      {open ? (
        <div
          className="absolute z-20 mt-1 w-full rounded-md border shadow"
          style={{
            background: 'var(--bg-surface)',
            borderColor: 'var(--border)',
            color: 'var(--text-main)',
            maxHeight: 220,
            overflowY: 'auto',
          }}
        >
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm" style={{ color: 'var(--text-muted)' }}>
              No matches
            </div>
          ) : null}
          {options.map((o, idx) => (
            <button
              key={o.id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                pick(o);
              }}
              onMouseEnter={() => setHighlight(idx)}
              className="block w-full px-3 py-2 text-left text-sm"
              style={{
                background: idx === highlight ? 'var(--accent)' : 'transparent',
                color: idx === highlight ? 'var(--bg-primary, #fff)' : 'var(--text-main)',
              }}
            >
              {o.name}{' '}
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                {o.category}
              </span>
            </button>
          ))}
          {showCreate ? (
            <button
              type="button"
              data-testid={testId ? `${testId}-create` : 'ingredient-create-action'}
              onMouseDown={(e) => {
                e.preventDefault();
                onCreateRequested?.(trimmed);
                setOpen(false);
              }}
              className="block w-full border-t px-3 py-2 text-left text-sm"
              style={{
                borderColor: 'var(--border)',
                color: 'var(--accent)',
                fontWeight: 500,
              }}
            >
              + Create &quot;{trimmed}&quot;
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}